<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import AbTestDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/AbTestDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import CampaignAnalyticsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/CampaignAnalyticsDialog.vue';
import AbComparisonDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/AbComparisonDialog.vue';
import CampaignIntelligencePanel from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/CampaignIntelligencePanel.vue';
import WhatsAppCampaignOperationsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignOperationsDialog.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import CampaignsAPI from 'dashboard/api/campaigns';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const route = useRoute();

const selectedCampaign = ref(null);
const selectedSuppressionCampaign = ref(null);
const campaignSuppressions = ref([]);
const suppressionMeta = ref({});
const isFetchingSuppressions = ref(false);
const [showAbTestDialog, toggleAbTestDialog] = useToggle();
const [showAnalyticsDialog, toggleAnalyticsDialog] = useToggle();
const [showAbComparisonDialog, toggleAbComparisonDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const hasFetchError = computed(() => uiFlags.value.hasFetchError);
const meta = computed(() => getters['campaigns/getMeta'].value);

const confirmDeleteCampaignDialogRef = ref(null);
const operationsDialogRef = ref(null);
const searchQuery = ref('');

const selectedStatus = ref('all');
const selectedSort = ref('date_desc');
const selectedTemplate = ref('all');
const selectedRisk = ref('all');
const selectedInboxFilter = ref(route.query.inbox_id || 'all');
const inboxSummary = ref(null);
const isFetchingSummary = ref(false);
const showIntelligence = ref(true);
const intelligenceTab = ref('health');

const selectedCampaignCategory = computed(() => {
  const category = route.params.campaignCategory || 'utility';
  return ['utility', 'marketing', 'all'].includes(category)
    ? category
    : 'utility';
});

const scopedCampaignCategory = computed(() =>
  ['utility', 'marketing'].includes(selectedCampaignCategory.value)
    ? selectedCampaignCategory.value
    : null
);

const categoryTitle = computed(() => {
  if (selectedCampaignCategory.value === 'marketing') {
    return t('CAMPAIGN.WHATSAPP.CATEGORY_TITLE.MARKETING');
  }
  if (selectedCampaignCategory.value === 'all') {
    return t('CAMPAIGN.WHATSAPP.CATEGORY_TITLE.ALL');
  }
  return t('CAMPAIGN.WHATSAPP.CATEGORY_TITLE.UTILITY');
});

const statusOptions = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.STATUS.ALL') || 'All Status',
    value: 'all',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.STATUS.COMPLETED') || 'Completed',
    value: 'completed',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.STATUS.DRAFT', 'Draft'),
    value: 'draft',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.STATUS.SCHEDULED') || 'Scheduled',
    value: 'scheduled',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.STATUS.SENDING', 'Sending'),
    value: 'sending',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.STATUS.CANCELLED', 'Cancelled'),
    value: 'cancelled',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.STATUS.FAILED', 'Failed'),
    value: 'failed',
  },
]);

const sortOptions = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.SORT.DATE_DESC') || 'Newest first',
    value: 'date_desc',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.SORT.DATE_ASC') || 'Oldest first',
    value: 'date_asc',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.SORT.NAME_ASC') || 'Name (A-Z)',
    value: 'name_asc',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.SORT.NAME_DESC') || 'Name (Z-A)',
    value: 'name_desc',
  },
]);

const riskOptions = computed(() => [
  { label: t('CAMPAIGN.WHATSAPP.FILTERS.RISK.ALL'), value: 'all' },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.RISK.HIGH_FAILURE'),
    value: 'high_failure',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.FILTERS.RISK.HAS_SUPPRESSIONS'),
    value: 'has_suppressions',
  },
]);

const selectedInboxId = computed(() =>
  selectedInboxFilter.value === 'all' ? null : selectedInboxFilter.value
);

const WhatsAppCampaigns = computed(() => {
  return getters['campaigns/getWhatsAppCampaigns'].value(selectedInboxId.value);
});

const templateOptions = computed(() => {
  const templates = new Set(
    WhatsAppCampaigns.value
      .map(campaign => campaign.template_params?.name)
      .filter(Boolean)
  );
  return [
    { label: t('CAMPAIGN.WHATSAPP.FILTERS.TEMPLATE.ALL'), value: 'all' },
    ...[...templates].sort().map(template => ({
      label: template.replace(/_/g, ' '),
      value: template,
    })),
  ];
});

const visibleWhatsAppCampaigns = computed(() => {
  return WhatsAppCampaigns.value.filter(campaign => {
    if (
      selectedTemplate.value !== 'all' &&
      campaign.template_params?.name !== selectedTemplate.value
    ) {
      return false;
    }
    if (selectedRisk.value === 'has_suppressions') {
      return Number(campaign.suppression_count || 0) > 0;
    }
    if (selectedRisk.value === 'high_failure') {
      const total = Number(campaign.campaign_metrics?.total || 0);
      const failed = Number(campaign.campaign_metrics?.failed || 0);
      return total > 0 && failed / total >= 0.1;
    }
    return true;
  });
});

const inboxes = useMapGetter('inboxes/getInboxes');
const selectedInbox = computed(() => {
  if (!selectedInboxId.value) return null;
  return inboxes.value.find(i => i.id === Number(selectedInboxId.value));
});

const inboxOptions = computed(() => [
  {
    value: 'all',
    label: t('CAMPAIGN.WHATSAPP.FILTERS.INBOX.ALL'),
  },
  ...inboxes.value
    .filter(
      inbox => inbox.channel_type === 'Channel::Whatsapp' && !inbox.disabled
    )
    .map(inbox => ({
      value: inbox.id,
      label: inbox.name,
    })),
]);

const operationInboxOptions = computed(() =>
  inboxOptions.value.filter(option => option.value !== 'all')
);

const fetchCampaigns = (page = 1) => {
  const params = {
    page,
    q: searchQuery.value,
    status: selectedStatus.value,
    sort_by: selectedSort.value,
    channel: 'whatsapp',
    whatsapp_template_category: selectedCampaignCategory.value,
  };
  if (selectedInboxId.value) {
    params.inbox_id = selectedInboxId.value;
  }
  store.dispatch('campaigns/get', params);
};

const fetchInboxSummary = async () => {
  if (!selectedInboxId.value) {
    inboxSummary.value = null;
    return;
  }

  isFetchingSummary.value = true;
  try {
    const response = await CampaignsAPI.whatsappInboxSummaries();
    inboxSummary.value = (response.data?.inboxes || []).find(
      summary => Number(summary.inbox_id) === Number(selectedInboxId.value)
    );
  } catch {
    inboxSummary.value = null;
  } finally {
    isFetchingSummary.value = false;
  }
};

const syncInboxQuery = () => {
  const nextInboxId = selectedInboxId.value;
  const currentInboxId = route.query.inbox_id || null;
  if (String(currentInboxId || '') === String(nextInboxId || '')) return;

  const query = { ...route.query };
  if (nextInboxId) {
    query.inbox_id = nextInboxId;
  } else {
    delete query.inbox_id;
  }

  router.replace({ name: route.name, params: route.params, query });
};

onMounted(() => {
  fetchCampaigns();
  fetchInboxSummary();
});

watch(
  [selectedCampaignCategory, selectedInboxId, selectedStatus, selectedSort],
  () => {
    syncInboxQuery();
    fetchCampaigns(1);
    fetchInboxSummary();
  }
);

watch(
  () => route.query.inbox_id,
  inboxId => {
    selectedInboxFilter.value = inboxId || 'all';
  }
);

const goBack = () => {
  router.push({ name: 'campaigns_whatsapp_index' });
};

let searchTimeout = null;
const handleSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    fetchCampaigns(1);
  }, 500);
};

const handlePageChange = p => {
  fetchCampaigns(p);
};

const hasNoWhatsAppCampaigns = computed(
  () => meta.value.totalCount === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleCampaignClick = campaign => {
  if (campaign.campaign_status !== 'completed') {
    if (campaign.lifecycle_permissions?.editable) {
      router.push({
        name: 'campaigns_whatsapp_edit',
        params: {
          inboxId: campaign.inbox?.id || selectedInboxId.value,
          campaignId: campaign.id,
        },
        query: { category: selectedCampaignCategory.value },
      });
    }
    return;
  }

  selectedCampaign.value = campaign;
  if (campaign.variant_group_id) {
    toggleAbComparisonDialog(true);
  } else {
    toggleAnalyticsDialog(true);
  }
};

const handleCloseAnalytics = () => {
  toggleAnalyticsDialog(false);
  selectedCampaign.value = null;
};

const handleCloseAbComparison = () => {
  toggleAbComparisonDialog(false);
  selectedCampaign.value = null;
};

const handleDuplicate = campaign => {
  router.push({
    name: 'campaigns_whatsapp_new',
    query: {
      duplicate_from: campaign.id,
      category:
        campaign.whatsapp_template_category || selectedCampaignCategory.value,
      inbox_id: campaign.inbox?.id || selectedInboxId.value,
    },
  });
};

const handleSuppressions = async campaign => {
  selectedSuppressionCampaign.value = campaign;
  campaignSuppressions.value = [];
  suppressionMeta.value = {};
  isFetchingSuppressions.value = true;
  try {
    const response = await CampaignsAPI.suppressions(campaign.id);
    campaignSuppressions.value = response.data?.suppressions || [];
    suppressionMeta.value = response.data?.meta || {};
  } catch {
    campaignSuppressions.value = [];
    suppressionMeta.value = {};
  } finally {
    isFetchingSuppressions.value = false;
  }
};

const handleCreateClick = () => {
  const query = { category: selectedCampaignCategory.value };
  if (selectedInboxId.value) {
    query.inbox_id = selectedInboxId.value;
  }
  router.push({
    name: 'campaigns_whatsapp_new',
    query,
  });
};

const handleAbTestClick = () => {
  toggleAbTestDialog();
};

const handleOperationsClick = () => {
  operationsDialogRef.value?.dialogRef.open();
};

const handleDraftsCreated = () => {
  fetchCampaigns(1);
};

const openIntelligenceTab = tab => {
  intelligenceTab.value = tab;
  showIntelligence.value = true;
};

const healthTone = quality => {
  const normalized = String(quality || '').toLowerCase();
  if (['green', 'high'].includes(normalized))
    return 'text-n-teal-11 bg-n-teal-3';
  if (['yellow', 'medium'].includes(normalized)) {
    return 'text-n-amber-11 bg-n-amber-3';
  }
  if (['red', 'low'].includes(normalized)) return 'text-n-ruby-11 bg-n-ruby-3';
  return 'text-n-slate-11 bg-n-alpha-2';
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-4 bg-n-surface-1 border-b sm:px-6 border-n-weak"
    >
      <div class="w-full max-w-7xl mx-auto py-4">
        <div
          class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
        >
          <div class="flex items-start gap-2 min-w-0 flex-1">
            <Button
              v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.BACK')"
              icon="i-lucide-arrow-left"
              size="sm"
              variant="ghost"
              color-scheme="secondary"
              class="flex-shrink-0 mt-0.5"
              @click="goBack"
            />
            <div class="min-w-0">
              <span class="block text-heading-1 text-n-slate-12 truncate">
                {{ categoryTitle }}
              </span>
              <span
                v-if="selectedInbox"
                class="block mt-1 text-sm text-n-slate-11 truncate"
              >
                {{ selectedInbox.name }}
              </span>
            </div>
          </div>
          <div
            class="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 lg:flex lg:w-auto lg:items-center lg:justify-end"
          >
            <div
              v-on-clickaway="() => toggleAbTestDialog(false)"
              class="relative min-w-0"
            >
              <Button
                v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.AB_TEST')"
                :label="t('CAMPAIGN.WHATSAPP.AB_TEST.BUTTON')"
                icon="i-lucide-split"
                size="sm"
                variant="faded"
                color="slate"
                class="w-full lg:w-auto"
                @click="handleAbTestClick"
              />
              <AbTestDialog
                v-if="showAbTestDialog"
                :whatsapp-template-category="scopedCampaignCategory"
                @close="toggleAbTestDialog(false)"
              />
            </div>
            <Button
              v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.OPERATIONS')"
              :label="t('CAMPAIGN.WHATSAPP.OPERATIONS.BUTTON', 'Operations')"
              icon="i-lucide-wand-sparkles"
              size="sm"
              variant="faded"
              color="slate"
              class="w-full lg:w-auto"
              @click="handleOperationsClick"
            />
            <Button
              v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.CREATE')"
              :label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
              icon="i-lucide-plus"
              size="sm"
              class="w-full lg:w-auto"
              @click="handleCreateClick"
            />
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 pt-4">
          <div
            v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.FILTER_INBOX')"
            class="min-w-0"
          >
            <Select v-model="selectedInboxFilter" :options="inboxOptions" />
          </div>
          <div
            v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.FILTER_STATUS')"
            class="min-w-0"
          >
            <Select v-model="selectedStatus" :options="statusOptions" />
          </div>
          <div
            v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.FILTER_SORT')"
            class="min-w-0"
          >
            <Select v-model="selectedSort" :options="sortOptions" />
          </div>
          <div
            v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.FILTER_TEMPLATE')"
            class="min-w-0"
          >
            <Select v-model="selectedTemplate" :options="templateOptions" />
          </div>
          <div
            v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.FILTER_RISK')"
            class="min-w-0"
          >
            <Select v-model="selectedRisk" :options="riskOptions" />
          </div>
          <div
            v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.SEARCH')"
            class="w-full min-w-56 sm:w-72"
          >
            <Input
              v-model="searchQuery"
              type="text"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.SEARCH_PLACEHOLDER') ||
                'Pesquisar campanha...'
              "
              icon="i-lucide-search"
              size="sm"
              @input="handleSearch"
            />
          </div>
        </div>
      </div>
    </header>
    <main class="flex-1 px-4 overflow-y-auto sm:px-6">
      <div class="w-full max-w-7xl mx-auto py-6">
        <section
          v-if="selectedInboxId"
          class="mb-4 border border-n-slate-4 rounded-lg bg-n-surface-2"
        >
          <div class="grid gap-3 p-4 md:grid-cols-5">
            <div class="min-w-0">
              <div class="text-xs font-medium text-n-slate-10">
                {{ t('CAMPAIGN.WHATSAPP.COMMAND.QUALITY') }}
              </div>
              <div
                class="inline-flex items-center px-2 py-1 mt-1 text-sm font-semibold rounded-md"
                :class="healthTone(inboxSummary?.latest_health?.quality_rating)"
              >
                {{
                  inboxSummary?.latest_health?.quality_rating ||
                  (isFetchingSummary ? '...' : '-')
                }}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-n-slate-10">
                {{ t('CAMPAIGN.WHATSAPP.COMMAND.TIER') }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  inboxSummary?.latest_health?.effective_messaging_limit ||
                  inboxSummary?.latest_health?.messaging_limit_tier ||
                  '-'
                }}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-n-slate-10">
                {{ t('CAMPAIGN.WHATSAPP.COMMAND.FAILED_30D') }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-slate-12">
                {{ inboxSummary?.last_30_days?.failed || 0 }}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-n-slate-10">
                {{ t('CAMPAIGN.WHATSAPP.COMMAND.OPT_OUTS') }}
              </div>
              <div class="mt-1 text-sm font-semibold text-n-ruby-11">
                {{ inboxSummary?.consent?.opted_out || 0 }}
              </div>
            </div>
            <div class="flex items-end justify-end gap-2">
              <Button
                v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.BLACKLIST')"
                :label="t('CAMPAIGN.WHATSAPP.COMMAND.BLACKLIST')"
                icon="i-lucide-shield-ban"
                size="sm"
                variant="faded"
                color="slate"
                @click="openIntelligenceTab('consent')"
              />
              <Button
                v-tooltip.top="t('CAMPAIGN.WHATSAPP.TOOLTIPS.INTELLIGENCE')"
                :label="
                  showIntelligence
                    ? t('CAMPAIGN.WHATSAPP.COMMAND.HIDE_DETAILS')
                    : t('CAMPAIGN.WHATSAPP.COMMAND.SHOW_DETAILS')
                "
                icon="i-lucide-panel-top"
                size="sm"
                variant="ghost"
                color-scheme="secondary"
                @click="showIntelligence = !showIntelligence"
              />
            </div>
          </div>
        </section>
        <CampaignIntelligencePanel
          v-if="selectedInboxId && showIntelligence"
          :key="`${selectedInboxId}-${intelligenceTab}`"
          :inbox-id="selectedInboxId"
          :initial-tab="intelligenceTab"
        />
        <div
          v-if="isFetchingCampaigns"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <div
          v-else-if="hasFetchError"
          class="flex flex-col items-center justify-center py-10 bg-n-ruby-3 rounded-lg border border-n-ruby-4 my-4"
        >
          <span class="text-n-ruby-11 font-medium mb-3">
            {{ t('CAMPAIGN.WHATSAPP.ERROR') }}
          </span>
          <Button
            label="Retry"
            icon="i-lucide-refresh-cw"
            size="sm"
            color="ruby"
            variant="flat"
            @click="fetchCampaigns(meta.currentPage)"
          />
        </div>
        <template v-else-if="!hasNoWhatsAppCampaigns">
          <div
            v-if="selectedStatus !== 'all'"
            class="flex items-center gap-2 mb-6"
          >
            <span class="text-sm font-medium text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP.FILTERS.ACTIVE') || 'Active Filters:'
            }}</span>
            <div
              class="flex items-center gap-1 px-3 py-1 bg-n-alpha-2 text-n-teal-11 rounded-full text-sm font-medium"
            >
              {{ t('CAMPAIGN.WHATSAPP.FILTERS.STATUS_LABEL') || 'Status:' }}
              {{ statusOptions.find(o => o.value === selectedStatus)?.label }}
              <button
                class="ml-1 hover:text-n-teal-12"
                @click="selectedStatus = 'all'"
              >
                <Icon icon="i-lucide-x" class="size-3.5" />
              </button>
            </div>
          </div>
          <CampaignList
            :campaigns="visibleWhatsAppCampaigns"
            is-whats-app-type
            @delete="handleDelete"
            @edit="handleCampaignClick"
            @duplicate="handleDuplicate"
            @suppressions="handleSuppressions"
          />
          <section
            v-if="selectedSuppressionCampaign"
            class="mt-4 border border-n-slate-4 rounded-lg bg-n-surface-2"
          >
            <div
              class="flex items-center justify-between gap-3 p-4 border-b border-n-slate-4"
            >
              <div class="min-w-0">
                <h3 class="text-sm font-semibold text-n-slate-12 truncate">
                  {{
                    t('CAMPAIGN.WHATSAPP.SUPPRESSIONS.TITLE', {
                      title: selectedSuppressionCampaign.title,
                    })
                  }}
                </h3>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{
                    t('CAMPAIGN.WHATSAPP.SUPPRESSIONS.TOTAL', {
                      count: suppressionMeta.total_count || 0,
                    })
                  }}
                </p>
              </div>
              <Button
                icon="i-lucide-x"
                size="sm"
                variant="ghost"
                color-scheme="secondary"
                @click="selectedSuppressionCampaign = null"
              />
            </div>
            <div v-if="isFetchingSuppressions" class="p-6 text-center">
              <Spinner />
            </div>
            <div
              v-else-if="!campaignSuppressions.length"
              class="p-6 text-sm text-center text-n-slate-11"
            >
              {{ t('CAMPAIGN.WHATSAPP.SUPPRESSIONS.EMPTY') }}
            </div>
            <div v-else class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead class="text-left bg-n-alpha-1 text-n-slate-10">
                  <tr>
                    <th class="px-3 py-2 font-medium">
                      {{ t('CAMPAIGN.WHATSAPP.SUPPRESSIONS.CONTACT') }}
                    </th>
                    <th class="px-3 py-2 font-medium">
                      {{ t('CAMPAIGN.WHATSAPP.SUPPRESSIONS.TYPE') }}
                    </th>
                    <th class="px-3 py-2 font-medium">
                      {{ t('CAMPAIGN.WHATSAPP.SUPPRESSIONS.SOURCE') }}
                    </th>
                    <th class="px-3 py-2 font-medium">
                      {{ t('CAMPAIGN.WHATSAPP.SUPPRESSIONS.REASON') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="suppression in campaignSuppressions"
                    :key="suppression.id"
                    class="border-t border-n-slate-4"
                  >
                    <td class="px-3 py-2">
                      <div class="font-medium text-n-slate-12">
                        {{ suppression.contact_name || '-' }}
                      </div>
                      <div class="text-xs text-n-slate-10">
                        {{ suppression.phone_number || '-' }}
                      </div>
                    </td>
                    <td class="px-3 py-2 text-n-slate-11">
                      {{ suppression.suppression_type }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-11">
                      {{ suppression.source || '-' }}
                    </td>
                    <td class="px-3 py-2 text-n-slate-11">
                      {{ suppression.reason || '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
        <WhatsAppCampaignEmptyState
          v-else
          :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
          :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
          class="pt-14"
        />
        <div
          v-if="!isFetchingCampaigns && !hasNoWhatsAppCampaigns"
          class="mt-4 border-t border-n-slate-3 pt-2"
        >
          <TableFooter
            :current-page="meta.currentPage"
            :total-count="meta.totalCount"
            :page-size="15"
            @page-change="handlePageChange"
          />
        </div>
      </div>
    </main>
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <WhatsAppCampaignOperationsDialog
      ref="operationsDialogRef"
      :inbox-id="selectedInboxId"
      :inbox-options="operationInboxOptions"
      :whatsapp-template-category="scopedCampaignCategory || 'utility'"
      @drafts-created="handleDraftsCreated"
    />
    <CampaignAnalyticsDialog
      v-if="showAnalyticsDialog && selectedCampaign"
      :campaign="selectedCampaign"
      @close="handleCloseAnalytics"
    />
    <AbComparisonDialog
      v-if="showAbComparisonDialog && selectedCampaign"
      :campaign="selectedCampaign"
      @close="handleCloseAbComparison"
    />
  </section>
</template>
